import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { toast } from "react-toastify";
import { convertPrice, getConfigByCountryCode } from "./localization";
import * as icons from "simple-icons";

export const ERRORS = {
  INVALID_EMAIL: "Please enter a valid email.",
  REQUIRED_FIELD: "Please fill all fields.",
  UNMATCHED_STRING: "Both passwords do not match.",
  INVALID_OTP: "Please enter a 4-digits valid otp.",
};

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

export const getPdfPageCount = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdf.numPages;
};

export const formatToHMS = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  if (secs === 60) {
    const adjustedMins = mins + 1;
    const adjustedHrs = hrs + Math.floor(adjustedMins / 60);
    return `${adjustedHrs.toString().padStart(2, "0")}:${(adjustedMins % 60)
      .toString()
      .padStart(2, "0")}:00`;
  }

  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const parseHMStoSeconds = (hms) => {
  const [hours, minutes, seconds] = hms.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const formatToTimestamp = (seconds) => {
  const date = new Date(seconds * 1000);
  return date.toISOString();
};

export const validateVideoDuration = (file, max, min = 0, message) => {
  const video = document.createElement("video");
  video.preload = "metadata";

  const onLoadedMetaDataPromise = new Promise((resolve) => {
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
      video.remove();
    };
  });

  video.src = window.URL.createObjectURL(file);

  return onLoadedMetaDataPromise
    .then((res) => {
      if (res <= max && res >= min) {
        return true;
      } else {
        toast.error(
          message
            ? message
            : `Video must be a length of ${max} seconds or less`,
        );
        return false;
      }
    })
    .catch((e) => {
      return false;
    });
};

export const durationToSeconds = (duration) => {
  if (!duration) return 0;
  const [h = 0, m = 0, s = 0] = duration.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

export const secondsToFormatted = (totalSec) => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [];
  if (h) parts.push(`${h} hr`);
  if (m) parts.push(`${m} min`);
  if (s) parts.push(`${s} sec`);
  return parts.join(" ");
};

export const queryParams = (data) => {
  if (data) {
    let queryStrings = Object.entries(data).map(([key, value]) => {
      if (value) {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
      return "";
    });

    queryStrings = queryStrings.filter((item) => item !== "");
    queryStrings = queryStrings?.length ? queryStrings.join("&") : "";

    return `?${queryStrings}`;
  }

  return ``;
};

export const getQueryParams = (search) => {
  const params = new URLSearchParams(search);
  const queryParams = {};

  for (let [key, value] of params.entries()) {
    queryParams[key] = value;
  }

  return queryParams;
};

export const calculateCartAmount = async (cart, countryCode = "GB") => {
  let total_amount = 0;

  if (cart && cart.length > 0) {
    const { currency } = getConfigByCountryCode(countryCode);

    const amounts = await Promise.all(
      cart.map(async (item) => {
        if (!item?.price?.amount) return 0;
        return await convertPrice(
          item.price.amount,
          item.price.currency,
          currency,
        );
      }),
    );

    total_amount = amounts.reduce((a, b) => a + b, 0);
  }

  return { total_amount };
};

export const getVideoDuration = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(formatToHMS(Math.round(video.duration)));
    };
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(null);
    };
    video.src = URL.createObjectURL(file);
  });
};

export const getMultipleVideoDurations = (videoUrls) => {
  const promises = videoUrls.map((url) => getVideoDuration(url));
  return Promise.all(promises);
};

export const getTotalVideoDurationString = (videoUrls) => {
  let urls = videoUrls?.filter((item) => item !== "");

  if (urls?.length > 0) {
    return getMultipleVideoDurations(urls)
      .then((durations) => {
        const totalDuration = durations.reduce(
          (total, duration) => total + duration,
          0,
        );
        const totalHours = Math.floor(totalDuration / 3600);
        const totalMinutes = Math.floor((totalDuration % 3600) / 60);
        const totalSeconds = Math.floor(totalDuration % 60);

        const formatted = [];
        if (totalHours > 0) {
          formatted.push(`${totalHours} hours`);
        }
        if (totalMinutes > 0 || totalHours > 0) {
          formatted.push(`${totalMinutes} minutes`);
        }
        formatted.push(`${totalSeconds} seconds`);

        return formatted.join(" and ");
      })
      .catch((error) => {
        console.error("Error:", error);
        throw error;
      });
  }

  return "00:00";
};

export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const getTime = (date) => {
  dayjs.extend(relativeTime);
  return dayjs(date).fromNow();
};

export const formatDate = (date, time) => {
  const dayjsdate = dayjs(date);
  let formattedDate = dayjsdate.format("DD MMMM, YYYY");

  if (time) {
    formattedDate = dayjsdate.format("DD MMMM, YYYY HH:mm");
  }

  return formattedDate;
};

export const compareString = (first, second) => {
  let matched = first === second;

  if (!matched) {
    toast.error(ERRORS.UNMATCHED_STRING);
  }

  return matched;
};

export const objectValidator = (object, message) => {
  if (object && Object.entries(object).length > 0) {
    let array = Object.entries(object).map((item) => {
      if (!item[1] || item[1] === null || item[1] === undefined) {
        return false;
      } else {
        return true;
      }
    });

    let isNull = array.includes(false);

    if (isNull) {
      toast.error(message || ERRORS.REQUIRED_FIELD);
    }

    return !isNull;
  }

  toast.error(message || ERRORS.REQUIRED_FIELD);
  return false;
};

export const convertToBytes = (sizeStr) => {
  const units = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
  };

  const match = sizeStr.match(/([\d.]+)\s*(B|KB|MB|GB)/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  return value * (units[unit] || 1);
};

export function setMetaTags(title, description) {
  document.title = title;
  const metaDescription = document.querySelector("meta[name='description']");
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  }
}

export function formatToMonthDay(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export const getZoneColor = (label = "") => {
  const normalized = label.trim().toLowerCase();

  if (["poor", "pobre", "malo", "débil"].includes(normalized))
    return "#ff4d4f33";

  if (
    ["below average", "Debajo del promedio", "inferior al promedio"].includes(
      normalized,
    )
  )
    return "#ff9f4033";

  if (["average", "promedio", "medio"].includes(normalized)) return "#fadb1433";

  if (["good", "bueno"].includes(normalized)) return "#52c41a33";

  if (["excellent", "excelente"].includes(normalized)) return "#1890ff33";

  if (["underweight", "bajo peso", "delgado"].includes(normalized))
    return "#85C1E933";

  if (["normal", "normalidad"].includes(normalized)) return "#52c41a33";

  if (["overweight", "sobrepeso"].includes(normalized)) return "#FFD70033";

  if (["obese", "obesidad", "obeso"].includes(normalized)) return "#ff4d4f33";

  return "#99999933";
};

export const getLastFiveDaysLabels = () => {
  return Array.from({ length: 5 }, (_, i) =>
    dayjs()
      .subtract(4 - i, "day")
      .format("MMM D"),
  );
};

export const getChartDateRange = (type) => {
  const endDate = dayjs().format("YYYY-MM-DD");

  let startDate;
  if (type === "week") {
    startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
  } else if (type === "month") {
    startDate = dayjs().subtract(1, "month").format("YYYY-MM-DD");
  }

  return { startDate, endDate };
};

export const getLastNDaysLabels = (n) => {
  const labels = [];
  for (let i = n - 1; i >= 0; i--) {
    labels.push(dayjs().subtract(i, "day").format("MMM D"));
  }
  return labels;
};

export const getDeviceId = () => {
  let deviceId = localStorage.getItem("device_token");
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem("device_token", deviceId);
  }
  return deviceId;
};

export const formatAISuggestion = (text) => {
  if (!text) return "";

  let formatted = text;

  formatted = formatted.replace(/\\n/g, "\n");

  // Bold **text**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Convert numbered list: 1. text
  formatted = formatted.replace(
    /(?:^|\n)(\d+)\.\s(.+)/g,
    "<br/><strong>$1.</strong> $2",
  );

  // Convert dash bullets: - text
  formatted = formatted.replace(/(?:^|\n)-\s(.+)/g, "<br/>• $1");

  // Line breaks
  formatted = formatted.replace(/\n/g, "<br/>");

  return formatted;
};

export const getBrandIcon = (url = "") => {
  const lowerUrl = url.toLowerCase();

  const brands = {
    canva: icons.siCanvas,
    shopify: icons.siShopify,
    calendar: icons.siGooglecalendar,
    zoom: icons.siZoom,
    google: icons.siGooglemeet,
    youtube: icons.siYoutube,
    instagram: icons.siInstagram,
    tiktok: icons.siTiktok,
    linkedin: icons.siLinkerd,
    facebook: icons.siFacebook,
    whatsapp: icons.siWhatsapp,
    telegram: icons.siTelegram,
    discord: icons.siDiscord,
    slack: icons.siSlackware,
    notion: icons.siNotion,
    stripe: icons.siStripe,
    github: icons.siGithub,
  };

  const matchedKey = Object.keys(brands).find((key) => lowerUrl.includes(key));

  return matchedKey ? brands[matchedKey] : null;
};

export const selectorStyle = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "12px",
    padding: "4px",
    boxShadow: "none",
    minHeight: "44px",

    "&:hover": {
      border: "1px solid rgba(255,255,255,0.4)",
    },
  }),

  singleValue: (base) => ({
    ...base,
    color: "#fff",
  }),

  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.6)",
  }),

  input: (base) => ({
    ...base,
    color: "#fff",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base) => ({
    ...base,
    color: "#fff",
    "&:hover": {
      color: "#fff",
    },
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999,
    backgroundColor: "rgba(20,20,20,0.95)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "12px",
    overflow: "hidden",
  }),

  menuList: (base) => ({
    ...base,
    maxHeight: "200px",
    backgroundColor: "transparent",
  }),

  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected
      ? "#2f3542" // selected dark
      : isFocused
        ? "#1f2937" // hover dark
        : "transparent",
    color: "#fff",
    cursor: "pointer",

    ":active": {
      backgroundColor: "#374151",
    },
  }),
};
